import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../../services/api';
import '../../css/Login.css';
import '../../css/ResetPassword.css';
import { useTranslation } from 'react-i18next';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [passwordReset, setPasswordReset] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { t } = useTranslation(['auth']);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      message.error(t('auth:reset.invalid_link_toast'));
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError(t('auth:reset.invalid_link_toast'));
      return;
    }

    if (!password || !password.trim()) {
      setError(t('auth:reset.new_password_required'));
      return;
    }

    if (password.length < 6) {
      setError(t('auth:reset.new_password_min'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth:reset.confirm_mismatch'));
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword(token, password);
      setPasswordReset(true);
      message.success(t('auth:reset.success_toast'));
    } catch (error) {
      const errorMessage = error.response?.data?.message || t('auth:reset.fail_toast');
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'password') {
      setPassword(value);
    } else if (field === 'confirmPassword') {
      setConfirmPassword(value);
    }
    if (error) {
      setError('');
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const renderFooter = () => (
    <div className="auth-footer">
      <p>
        <a href="/en/terms-of-service">Terms of Service</a>
        {' | '}
        <a href="/en/privacy">Privacy Policy</a>
      </p>
    </div>
  );

  if (!tokenValid) {
    return (
      <div className="login-page">
        <div className="auth-container" style={{ maxWidth: '36rem' }}>
          <div className="auth-card" style={{ minHeight: 'auto' }}>
            <div className="auth-form-panel">
              <div className="auth-form-content">
                <div className="reset-status-content">
                  <div className="reset-status-icon reset-status-icon--error">&#128274;</div>
                  <h2>{t('auth:reset.invalid_title')}</h2>
                  <p>{t('auth:reset.invalid_desc')}</p>
                  <button
                    type="button"
                    className="submit-btn"
                    onClick={() => navigate('/forgot-password')}
                  >
                    {t('auth:reset.reapply')}
                  </button>
                </div>
              </div>
            </div>
          </div>
          {renderFooter()}
        </div>
      </div>
    );
  }

  if (passwordReset) {
    return (
      <div className="login-page">
        <div className="auth-container" style={{ maxWidth: '36rem' }}>
          <div className="auth-card" style={{ minHeight: 'auto' }}>
            <div className="auth-form-panel">
              <div className="auth-form-content">
                <div className="reset-status-content">
                  <div className="reset-status-icon reset-status-icon--success">&#10003;</div>
                  <h2>{t('auth:reset.success_title')}</h2>
                  <p>{t('auth:reset.success_desc')}</p>
                  <button
                    type="button"
                    className="submit-btn"
                    onClick={handleBackToLogin}
                  >
                    {t('auth:reset.login_now')}
                  </button>
                </div>
              </div>
            </div>
          </div>
          {renderFooter()}
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="auth-container" style={{ maxWidth: '36rem' }}>
        <div className="auth-card" style={{ minHeight: 'auto' }}>
          <div className="auth-form-panel">
            <div className="auth-form-content">
              <form onSubmit={handleSubmit} className="auth-form">
                <button
                  type="button"
                  className="forgot-password-back-link"
                  onClick={handleBackToLogin}
                >
                  &larr; {t('auth:reset.back_login')}
                </button>
                <h1 className="forgot-password-title">{t('auth:reset.title')}</h1>
                <p className="forgot-password-desc">{t('auth:reset.desc')}</p>

                <div className="input-group">
                  <div className="reset-password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('auth:reset.placeholder_new')}
                      value={password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className="auth-input"
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      className="reset-password-eye-icon"
                    >
                      {showPassword ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                    </span>
                  </div>
                </div>

                <div className="input-group">
                  <div className="reset-password-input-wrapper">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder={t('auth:reset.placeholder_confirm')}
                      value={confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      className="auth-input"
                    />
                    <span
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="reset-password-eye-icon"
                    >
                      {showConfirmPassword ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                    </span>
                  </div>
                </div>

                {error && (
                  <div className="error-message">
                    {error}
                  </div>
                )}

                <button type="submit" disabled={loading} className="submit-btn">
                  {t('auth:reset.submit')}
                </button>
              </form>
            </div>
          </div>
        </div>
        {renderFooter()}
      </div>
    </div>
  );
};

export default ResetPassword; 